/**
 * Client-side mirror of the add-on billing config in the API's api/stripe/stripe.const.js
 * (BILLING_PRODUCTS / PURCHASING_PLAN_TIERS).
 *
 * Everything here is DISPLAY ONLY. The backend never trusts a price, amount or interval
 * price-id from the client - it resolves the real Stripe Price server-side from
 * {tier, interval}. Keep the figures in sync with the API so the UI doesn't quote a
 * number we don't actually charge, but understand that a mismatch is a cosmetic bug, not
 * a billing one.
 */

export const BILLING_INTERVAL = {
    MONTH: "month",
    YEAR: "year",
}

/**
 * Annual plans are 25% off the monthly-equivalent yearly cost. This is baked into a
 * separate Stripe Price rather than applied as a coupon, so it stacks with promo codes
 * instead of being replaced by one.
 */
export const ANNUAL_DISCOUNT_RATE = 0.25

/**
 * Length of the Pro trial. Mirrors STRIPE.TRIAL_PERIOD_DAYS in the API - the server sets
 * the real trial on the Stripe subscription, this is only for quoting the end date. It
 * starts on a card, which is charged when it ends unless the trial is cancelled first.
 */
export const PRO_TRIAL_DAYS = 14

/**
 * Length of an add-on (Purchasing / Expenses) trial and the hard cap it runs under -
 * always the Business tier, never Scale, and ADDON_TRIAL_QUOTA units for its duration
 * rather than the tier's own quota. It starts on a card and converts to the paid tier
 * on it when it ends, unless cancelled first. Mirror STRIPE.ADDON_TRIAL_PERIOD_DAYS / ADDON_TRIAL_QUOTA
 * in the API, which is what actually enforces both.
 */
export const ADDON_TRIAL_DAYS = 14
export const ADDON_TRIAL_QUOTA = 50

export const BILLING_PRODUCT = {
    PURCHASING: "purchasing",
    EXPENSES: "expenses",
    EQUIPMENT: "equipment",
    POWER_AI: "powerAi",
}

/**
 * Everything a person can trial, exactly once each - the keys of person.trialsUsed and
 * org.trialsUsed (see utils/org-plan.js canStartTrial). Mirrors STRIPE.TRIAL_PRODUCT.
 */
export const TRIAL_PRODUCT = {
    PRO: "pro",
    ...BILLING_PRODUCT,
}

/**
 * Trials started before this moment don't count: every org and person gets each trial
 * once more from here. Only trialsUsedAt (when a trial started) is compared with it; the
 * older markers carry no date and are ignored. Mirrors STRIPE.TRIALS_RESET_AT in the API.
 */
export const TRIALS_RESET_AT = Date.UTC(2026, 9, 4) // 2026-10-04

/**
 * Stripe subscription statuses under which an add-on counts as "live" - the org is on
 * the tier it bought, sees the manage panel instead of the pricing table, and is
 * gatekept on that tier's quota/features. past_due is included so a failed renewal
 * degrades to a billing warning (see billingIssues.js), not an instant downgrade.
 */
export const BILLING_LIVE_STATUSES = ["active", "trialing", "past_due"]

/** @deprecated Use BILLING_LIVE_STATUSES - the list was never purchasing-specific. */
export const PURCHASING_LIVE_STATUSES = BILLING_LIVE_STATUSES

/**
 * Purchasing allowance bundled with an active Pro licence (org.isLicensedActive). Not a
 * Stripe product - there's nothing to buy or manage - so it's kept out of
 * PURCHASING_TIERS, which only lists purchasable tiers. Used for the pricing table /
 * tile and for gatekeeping the invoice quota.
 */
export const PURCHASING_FREE_TIER = {
    id: "free",
    name: "Free",
    monthly: 0,
    annual: 0,
    overage: 0,
    includedQuota: 50,
    // Deliberately not in `features` below: the allowance an org actually gets depends on
    // the org (AppSumo gets APP_SUMO_FREE_TIER_QUOTA), so the tile renders this from
    // getAddonFreeQuota() rather than quoting a fixed number at everyone.
    quotaFeature: (quota) => `Up to ${quota} AI-processed invoices every month`,
    features: [
        "Automated vendor + PO matching",
        "Complete vendor, PO and invoice management",
        "Manual approval",
        "Vendor setup portal",
    ],
}

/**
 * Bundled Free allowance for an org on an AppSumo lifetime deal, for every add-on. Those
 * orgs paid once for a per-seat deal that predates these add-ons and carries no recurring
 * revenue to fund the AI calls each processed invoice / scanned receipt costs, so they get
 * a taste rather than the full Free allowance. Only Free is reduced - an AppSumo org that
 * buys Business/Scale gets what it bought.
 *
 * Display only, like the rest of this file. APP_SUMO_FREE_TIER_QUOTA in the API's
 * api/stripe/stripe.const.js is what actually caps them - keep the two in sync.
 */
export const APP_SUMO_FREE_TIER_QUOTA = 10

/**
 * `id` is the only field here the server reads back - it's posted as `tier` and resolved
 * against PURCHASING_PLAN_TIERS in the API's api/stripe/stripe.const.js, so these keys
 * must match that enum exactly. Renamed 2026-09-20 (pro -> business, business -> scale)
 * so a tier's key and its display name are the same word; `business` meant Scale before
 * that date.
 */
export const PURCHASING_TIERS = [
    {
        id: "business",
        name: "Business",
        monthly: 109,
        annual: 981, // 109 * 12 * 0.75
        overage: 0.15,
        includedQuota: 1000,
        recommended: false,
        features: [
            "Up to 1,000 AI-processed invoices every month",
            "Everything in Free plus:",
            "Automated workflows",
            "Open API access",
        ],
    },
    {
        id: "scale",
        name: "Scale",
        monthly: 299,
        annual: 2691, // 299 * 12 * 0.75
        overage: 0.1,
        includedQuota: 3000,
        recommended: true,
        features: [
            "Up to 3,000 AI-processed invoices every month",
            "Everything in Business plus:",
            "Open API access",
            "Priority suppport",
        ],
    },
]

/**
 * AI Expenses allowance bundled with an active paid licence. Same shape and same reason
 * as PURCHASING_FREE_TIER: not a Stripe product, so it stays out of EXPENSES_TIERS.
 *
 * Unlike Purchasing, Expenses has no overage price - going over the quota is refused
 * (expense_ai/monthly_limit_reached), not billed, because every scanned receipt costs us
 * a Document AI call. That is why no tier here carries an `overage`.
 *
 * includedQuota counts RECEIPTS EXTRACTED, not pages uploaded or files scanned: one photo
 * of a sheet holding fifty receipts becomes fifty expenses and spends fifty of the
 * allowance - which is a whole month of Free. The API meters the same way - see createExpensesFromReceiptsMission.
 */
export const EXPENSES_FREE_TIER = {
    id: "free",
    name: "Free",
    monthly: 0,
    annual: 0,
    includedQuota: 50,
    // See PURCHASING_FREE_TIER.quotaFeature - rendered from the org's resolved allowance.
    quotaFeature: (quota) => `Up to ${quota} AI-scanned receipts every month`,
    features: [
        "Snap, drop or email in a receipt",
        "Amount, date, tax and vendor read automatically",
        "Several receipts read off a single page",
        "Manual entry always available",
    ],
}

export const EXPENSES_TIERS = [
    {
        id: "business",
        name: "Business",
        monthly: 79,
        annual: 711, // 79 * 12 * 0.75
        includedQuota: 1000,
        recommended: false,
        features: [
            "Up to 1,000 AI-scanned receipts every month",
            "Everything in Free plus:",
            "Expense codes matched by AI",
            "Open API access",
        ],
    },
    {
        id: "scale",
        name: "Scale",
        monthly: 249,
        annual: 2241, // 249 * 12 * 0.75
        includedQuota: 3000,
        recommended: true,
        features: [
            "Up to 3,000 AI-scanned receipts every month",
            "Everything in Business plus:",
            "Open API access",
            "Priority support",
        ],
    },
]

/**
 * Equipment add-on tiers, mirroring EQUIPMENT_PLAN_TIERS in the API's stripe.const.js.
 *
 * The quota is not a monthly count like the other add-ons: it caps how many active
 * (unarchived) pieces of equipment the org holds at once. Going over is refused
 * (equipment/limit_reached), never billed - archiving a piece frees its slot.
 *
 * Each tier also includes Map view loads a month (includedMapLoads, one per opening of
 * the tab). Free and trials stop at theirs; Core and Scale never do - loads over the
 * allowance are billed per load (mapOverage) after the month ends.
 */
export const EQUIPMENT_TRIAL_MAP_LOADS = 300

export const EQUIPMENT_FREE_TIER = {
    id: "free",
    name: "Free",
    monthly: 0,
    annual: 0,
    includedQuota: 25,
    includedMapLoads: 300,
    quotaFeature: (quota) => `Up to ${quota} pieces of equipment`,
    features: [
        "Live equipment map - 300 loads a month",
        "Mobile scan sign-offs",
        "Geolocated check-outs and returns",
        "Equipment types, cost and bill rates",
        "Due-date and expiry alerts (in-app)",
    ],
}

export const EQUIPMENT_TIERS = [
    {
        id: "core",
        name: "Core",
        monthly: 49,
        annual: 441, // 49 * 12 * 0.75
        includedQuota: 250,
        includedMapLoads: 1000,
        mapOverage: 0.02,
        recommended: false,
        features: [
            "Up to 250 pieces of equipment",
            "1,000 map loads a month",
            "Everything in Free plus:",
            "Email alerts as well as in-app",
            "Check-out / check-in with overdue reminders",
            "Maintenance requests that block bookings",
            "Calibration and service blocking",
            "Booking approvals",
            "Unbilled usage alerts",
            "Open API access",
        ],
    },
    {
        id: "scale",
        name: "Scale",
        monthly: 129,
        annual: 1161, // 129 * 12 * 0.75
        includedQuota: 1000,
        includedMapLoads: 3000,
        mapOverage: 0.015,
        recommended: true,
        features: [
            "Up to 1,000 pieces of equipment",
            "3,000 map loads a month",
            "Everything in Core plus:",
            "Approval rules by type and booking value, multi-step",
            "Restricted equipment with compliance approvers",
            "Escalations to managers for overdue and pending items",
            "Weekly digest email",
            "Open API access",
            "Priority support",
        ],
    },
]

/**
 * Power AI (custom dashboards and reports) tiers, mirroring POWER_AI_PLAN_TIERS in the
 * API's stripe.const.js. The caps shown on the plans tile are read live from the API
 * (GET /org/:id/power-ai/tiers, which an admin can change without a deploy); these are
 * the defaults it falls back to. Free comes with a paid plan (Pro or Enterprise), like
 * every add-on's Free; an unlicensed org sees the Power AI tab's upgrade placeholder.
 * `includedQuota` is AI generations per person a month.
 */
export const POWER_AI_FREE_TIER = {
    id: "free",
    name: "Free",
    monthly: 0,
    annual: 0,
    includedQuota: 20,
    maxDashboards: 1,
    // Charts, numbers and tables per dashboard; text blocks don't count.
    maxCharts: 3,
    exports: false,
    maxSchedules: 0,
    maxRecipientsPerSchedule: 0,
    // How often the numbers are recomputed, and how far back a widget may look.
    refreshMinutes: 60,
    maxRangeMonths: 6,
}

export const POWER_AI_TIERS = [
    {
        id: "core",
        name: "Core",
        monthly: 99,
        annual: 891, // 99 * 12 * 0.75
        includedQuota: 300,
        maxDashboards: null,
        maxCharts: null,
        exports: true,
        maxSchedules: 0,
        maxRecipientsPerSchedule: 0,
        refreshMinutes: 10,
        maxRangeMonths: null,
        recommended: false,
    },
    {
        id: "scale",
        name: "Scale",
        monthly: 229,
        annual: 2061, // 229 * 12 * 0.75
        includedQuota: 1000,
        maxDashboards: null,
        maxCharts: null,
        exports: true,
        maxSchedules: 25,
        maxRecipientsPerSchedule: 20,
        refreshMinutes: 5,
        maxRangeMonths: null,
        recommended: true,
    },
]

/** Plain-language features of a Power AI tier, from its (configured) allowances. */
export const powerAiTierFeatures = (tier) => {
    const n = (v) => Number(v || 0).toLocaleString("en-US")
    const list = [
        "Standard dashboards: utilization, budget vs actual, P&L, leaves",
        tier.maxDashboards
            ? `${n(tier.maxDashboards)} custom dashboard${tier.maxDashboards === 1 ? "" : "s"} each`
            : "Unlimited custom dashboards",
        tier.maxCharts ? `Up to ${n(tier.maxCharts)} charts per dashboard` : "Unlimited charts",
        `About ${n(tier.includedQuota)} AI generations a month per person`,
    ]
    if (tier.maxRangeMonths) list.push(`The last ${n(tier.maxRangeMonths)} months, by month`)
    else list.push("Any date range, down to the day")
    if (tier.refreshMinutes) {
        list.push(
            tier.refreshMinutes >= 60
                ? `Numbers refreshed every ${tier.refreshMinutes === 60 ? "hour" : `${n(tier.refreshMinutes / 60)} hours`}`
                : `Numbers refreshed every ${n(tier.refreshMinutes)} minutes`,
        )
    }
    if (tier.exports) list.push("PDF and Excel export")
    if (tier.maxSchedules) {
        list.push(`Scheduled report emails - ${n(tier.maxSchedules)} schedules, ${n(tier.maxRecipientsPerSchedule)} recipients each`)
    }
    return list
}

export const BILLING_PRODUCTS = {
    [BILLING_PRODUCT.PURCHASING]: {
        product: BILLING_PRODUCT.PURCHASING,
        label: "AI Purchasing",
        // Dot-path of this product's state on the org document, matching the API's
        // BILLING_PRODUCTS[...].orgPath.
        orgPath: "purchasing",
        // What this product's quota counts, in the words the change dialogs use - see
        // AddonChangeConfirm, which is how one component can speak for every add-on.
        unit: "invoices",
        unitSingular: "invoice",
        tiers: PURCHASING_TIERS,
        freeTier: PURCHASING_FREE_TIER,
        // The one tier a free trial is offered on - see ADDON_TRIAL_DAYS.
        trialTier: "business",
        trialDays: ADDON_TRIAL_DAYS,
        trialQuota: ADDON_TRIAL_QUOTA,
    },
    [BILLING_PRODUCT.EXPENSES]: {
        product: BILLING_PRODUCT.EXPENSES,
        label: "AI Expenses",
        orgPath: "expenses",
        unit: "AI-scanned receipts",
        unitSingular: "receipt",
        tiers: EXPENSES_TIERS,
        freeTier: EXPENSES_FREE_TIER,
        trialTier: "business",
        trialDays: ADDON_TRIAL_DAYS,
        trialQuota: ADDON_TRIAL_QUOTA,
    },
    [BILLING_PRODUCT.EQUIPMENT]: {
        product: BILLING_PRODUCT.EQUIPMENT,
        label: "Equipment",
        orgPath: "equipment",
        unit: "pieces of equipment",
        unitSingular: "piece of equipment",
        // A cap on what the org holds, not a monthly allowance - the change dialogs and
        // checkout word the quota off this.
        quotaIsCapacity: true,
        tiers: EQUIPMENT_TIERS,
        freeTier: EQUIPMENT_FREE_TIER,
        trialTier: "core",
        trialDays: ADDON_TRIAL_DAYS,
        trialQuota: ADDON_TRIAL_QUOTA,
    },
    [BILLING_PRODUCT.POWER_AI]: {
        product: BILLING_PRODUCT.POWER_AI,
        label: "Power AI",
        orgPath: "powerAi",
        unit: "AI generations per person",
        unitSingular: "AI generation",
        tiers: POWER_AI_TIERS,
        freeTier: POWER_AI_FREE_TIER,
        // No trial of its own: Free on a paid plan is the taste.
        trialTier: null,
        trialDays: 0,
        trialQuota: 0,
    },
}

export function getProductConfig(product) {
    return BILLING_PRODUCTS[product]
}

export function getTierConfig(product, tierId) {
    return getProductConfig(product)?.tiers?.find((t) => t.id === tierId)
}

/**
 * The per-month figure to show for a tier on a given interval - annual plans are quoted
 * per month ("$270/mo, billed $3,240/year") since that's the comparison customers make.
 */
export function getMonthlyDisplayAmount(tier, interval) {
    return interval === BILLING_INTERVAL.YEAR ? tier.annual / 12 : tier.monthly
}

/**
 * What an org saves per year by paying annually instead of monthly.
 */
export function getAnnualSaving(tier) {
    return tier.monthly * 12 - tier.annual
}

/**
 * mAX AI credits. 1 credit = $0.01 of actual API cost (Claude tokens + Document AI
 * pages). Every org person has their own wallet - credits are never transferable - and
 * billing (card, Stripe customer) belongs to the org.
 *
 * Packs are one-off purchases bought for one person, not a subscription tier. Credits
 * roll over and are valid for MAX_AI_CREDIT_VALIDITY_MONTHS from purchase. Display only:
 * the server resolves the real Stripe price from `id` (starter | pro | scale), never
 * from `price`.
 *
 * `credits` is the total a person receives (base + bonus); `bonusRate` is display only.
 */
export const MAX_AI_CREDIT_VALIDITY_MONTHS = 12
export const MAX_AI_CREDITS_PER_DOLLAR = 100

/**
 * Auto top-up (per wallet): buys `packId` again, off-session on the org's card, when the
 * balance drops below `threshold`. The threshold can't go under the highest per-run cap,
 * or a single Max run could drain the wallet before a top-up lands.
 *
 * The threshold and the monthly spend caps are dollars, as the API takes them
 * (`thresholdUsd`, `monthlyCapUsd`); the API stores them as credits.
 */
export const MAX_AI_AUTO_TOPUP_DEFAULTS = { enabled: false, packId: "starter", threshold: 5 }
export const MAX_AI_MIN_TOPUP_THRESHOLD = 5
export const MAX_AI_MAX_TOPUPS_PER_DAY = 3

// Credits are "expiring soon" inside this window - flagged on the balance column.
export const MAX_AI_EXPIRING_SOON_DAYS = 30

/**
 * Free "included" credits (never expire, used before purchased ones). Mirrors
 * MAX_CREDITS_PRO_SEAT_BONUS / MAX_CREDITS_TRIAL_BONUS* in the API, which grants them:
 *  - Every licensed org (Enterprise, paid Pro, AppSumo, a manual licence) but GT, and never to a
 *    gr.gt.com email in any org: once
 *    per person per org, half the Pro seat price ($12.99 admins + senior management /
 *    $9.99 project managers).
 *  - Pro trial with a card on file: to the first MAX_AI_TRIAL_BONUS_SEATS admins,
 *    senior managers or project managers who open mAX. Once per person, org and card.
 * Members, observers and externals never get free credits.
 */
export const MAX_AI_PRO_SEAT_BONUS = { admins: 650, managers: 500 }
export const MAX_AI_TRIAL_BONUS = 200
export const MAX_AI_TRIAL_BONUS_SEATS = 3

/** What a missionX admin may gift one person at a time from MxAdmin. Mirrors the API's MAX_CREDITS_ADMIN_GIFT. */
export const MAX_AI_ADMIN_GIFT = { min: 100, max: 300 }

/**
 * The levels a message runs at, mirrored from the API's MAX_CREDITS_MODES (in its
 * order). "deep" is the API's key for High, the one stored on every usage row.
 */
export const MAX_AI_MODES = [
    { id: "low", label: "Low" },
    { id: "standard", label: "Standard" },
    { id: "deep", label: "High" },
    { id: "max", label: "Max" },
]

export const MAX_AI_PACKS = [
    {
        id: "starter",
        name: "Starter",
        price: 20,
        credits: 1500,
        bonusRate: 0,
        description:
            "For help with missionX and mAX to complete things like timesheets, answer questions and generate smart contextualised work.",
    },
    {
        id: "pro",
        name: "Pro",
        price: 100,
        credits: 7800,
        bonusRate: 0.04,
        badge: "Most popular",
        description: "For the PM - helps them manage projects, complete work and automate most of their admin.",
    },
    {
        id: "scale",
        name: "Power Pro",
        price: 200,
        credits: 15700,
        bonusRate: 0.05,
        badge: "Best value",
        description: "Everything in the other packs, made for hard-core document processing, reporting, RFPs and more.",
    },
]

export function getMaxAiPack(packId) {
    return MAX_AI_PACKS.find((p) => p.id === packId)
}

/** Effective price per 1,000 credits, bonus included. */
export function getMaxAiCostPerThousand(pack) {
    return (pack.price / pack.credits) * 1000
}
